import React from "react";
import { FormFieldConfig } from "../../../../../Components/Common/CommonForm";
import { StudyTestItem, TestType } from "./studytest.types";
import { formatDate } from "../../../../../common/service/CommonService";
import { AlcoholResult_OPTIONS } from "../../../../../api/modules/common.option";
import { validations } from "../../../../../validations/FormValidations";

export const alcoholTest = [
  {
    key: "alcoholstage",
    label: "Stage",
  },
  {
    key: "bac",
    label: "Blood Alcohol Concentration (BAC)",
  },
  {
    key: "alcoholunit",
    label: "Unit",
  },
  {
    key: "indicatorlightcolor",
    label: "Indicator Light Color",
  },
  {
    key: "alcoholresulttext",
    label: "Result",
    options: AlcoholResult_OPTIONS,
  },
];

export const labTest = [
  {
    key: "reportname",
    label: "Report Name",
  },
  {
    key: "filename",
    label: "Attachment",
  },
  {
    key: "createddate",
    label: "Test Date",
    render: (value: string) => formatDate(value),
  },
  {
    key: "remarks",
    label: "Test Remarks",
  },
  {
    key: "verifiedby",
    label: "Verified By / Date",
    render: (_: any, row?: any) => {
      const isVerified = row && row.isverified;
      return (
        <>
          <p className="mb-0 fw-medium text-dark">{isVerified ? (row.verifiedbyname || "—") : "—"}</p>
          <p className="mb-0 text-muted fs-11">
            {isVerified && row.verifieddate ? formatDate(row.verifieddate) : "—"}
          </p>
        </>
      );
    },
  },
  {
    key: "verifystatus",
    label: "Verification Status",
    render: (_: any, row?: any) => {
      if (!row || !row.isverified) {
        return "—";
      }
      const status = row.verifystatus.toLowerCase();
      if (status === "pass") {
        return <span className="badge bg-success-subtle text-success border border-success px-2 py-1 fs-11">Pass</span>;
      }
      if (status === "fail") {
        return <span className="badge bg-danger-subtle text-danger border border-danger px-2 py-1 fs-11">Fail</span>;
      }
      return "—";
    },
  },
];

export const ecgTest = [
  {
    key: "testname",
    label: "Test Name",
  },
  {
    key: "remarks",
    label: "Test Remarks",
  },
  {
    key: "lastmodifiedby",
    label: "Last Modified By",
  },
  {
    key: "lastmodifieddate",
    label: "Last Modified Date",
  },
];
export const xrayTest = [
  {
    key: "testname",
    label: "Test Name",
  },
  {
    key: "remarks",
    label: "Test Remarks",
  },
  {
    key: "lastmodifiedby",
    label: "Last Modified By",
  },
  {
    key: "lastmodifieddate",
    label: "Last Modified Date",
  },
];


export const StudyTest: StudyTestItem[] = [
  {
    key: "alcohol",
    name: "Alcohol Test",
    icon: "ri-goblet-line",
    color: "text-primary",
  },
  {
    key: "ecg",
    name: "ECG",
    icon: "ri-heart-pulse-line",
    color: "text-warning",
  },
  {
    key: "lab",
    name: "Lab Test",
    icon: "ri-flask-line",
    color: "text-info",
  },
  {
    key: "xray",
    name: "X-Ray",
    icon: "ri-dashboard-3-line",
    color: "text-dark",
  },
];


export const testConfig = (
  AlcoholResult_OPTIONS: any[],
  alcoholTestsData: any,
  ecgTestsData: any,
  labTestsData: any,
  xrayTestsData: any
): Record<TestType, any> => ({
  alcohol: {
    columns: alcoholTest,
    data: alcoholTestsData,
    options: AlcoholResult_OPTIONS
  },
  ecg: {
    columns: ecgTest,
    data: ecgTestsData,
  },
  lab: {
    columns: labTest,
    data: labTestsData,
  },
  xray: {
    columns: xrayTest,
    data: xrayTestsData,
  },
});


export const medicalTestFields = (
  testType: "lab" | "ecg" | "xray",
  existingFile: any[],
  setPreviewFile: any,
  setPreviewUrl: any,
  setIsPreviewOpen: any,
  setExistingFile: any,
): FormFieldConfig[] => [
    ...(testType === "lab"
      ? [
        {
          name: "reportname",
          label: "Report Name",
          placeholder: "Enter Report Name",
          required: true,
          rules: validations.labtest.reportname,
        },
      ]
      : []),

    {
      name: "attachment",
      label: "Attachment Upload *",
      type: "file",
      required: !!(existingFile.length < 0),
      existingFiles: existingFile,

      onExistingView: (file: any) => {
        if (file?.file instanceof File) {
          setPreviewFile(file.file);
          setPreviewUrl(null);
          setIsPreviewOpen(true);
        } else if (file?.filepath) {
          const path = String(file.filepath);
          const fullUrl = path.startsWith("blob:") || path.startsWith("http://") || path.startsWith("https://")
            ? path
            : (sessionStorage.getItem("filebaseUrl") || "") + path;
          setPreviewFile(null);
          setPreviewUrl(fullUrl);
          setIsPreviewOpen(true);
        }
      },

      onExistingDelete: () => {
        setExistingFile([]);
      },

      rules: {
        validate: {
          fileType: (file: File) => {
            if (!file && existingFile.length > 0) return true;
            if (!file) return "File is required";

            const fileName = file.name.toLowerCase();

            const isAllowed =
              file.type === "application/pdf" ||
              fileName.endsWith(".pdf") ||
              file.type ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
              fileName.endsWith(".xlsx");

            return isAllowed || "Only PDF or XLSX files are allowed";
          },

          fileSize: (file: File) => {
            if (!file) return true;

            return (
              file.size <= 10 * 1024 * 1024 || "File must be under 10MB"
            );
          },
        },
      },
    },

    {
      name: "remarks",
      label: "Remarks / Result Summary",
      type: "textarea",
      placeholder: "Enter clinical remarks...",
      rules: validations.labtest.remarks,
    },
  ];
